import React, { useState, useEffect, useRef } from "react";
import CustomSelect from "../../features/customSelect/CustomSelect";
import CustomMultiSelect from "../../features/customMultiSelect/CustomMultiSelect";
import CustomCheckbox from "../../features/customCheckbox/CustomCheckbox";
import CustomMultiSelectPeriod from "../../features/customMultiSelect/CustomMultiSelectPeriod";
import GroupReportBarChart from "../../features/groupReportBarChart/GroupReportBarChart";
import GroupReportsPDF from "./GroupReportsPdf";
import { yearGroups, studentStatuses, typesOfIntervention, selectableSubjects } from "../../data/data";
import { getSchools, getTutors, getTutorGroups, getStudents } from "../../services/scpApi";
import { calcAcademicYearInterventionCompleteness, calcHadSomeLowAttendance } from "../../includes/utilities";


const GroupReports = () => {

    const [loading, setLoading] = useState(false);
    const [reportGenerated, setReportGenerated] = useState(false);
    const latestRequestIdRef = useRef(0);

    // schools
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState({});

    // year groups
    const [selectedYearGroup, setSelectedYearGroup] = useState({});

    // tutor group leads
    const [tutorGroupLeads, setTutorGroupLeads] = useState([]);
    const [selectedTutorGroupLead, setSelectedTutorGroupLead] = useState({});

    // tutor groups
    const [tutorGroups, setTutorGroups] = useState([]);
    const [selectedTutorGroup, setSelectedTutorGroup] = useState({});

    // Full intervention students
    const [includeFullYrIntervention, setIncludeFullYrIntervention] = useState(true);

    // partial intervention students
    const [includePartialYrIntervention, setIncludePartialYrIntervention] = useState(false);

    // no intervention students
    const [includeNoIntervention, setIncludeNoIntervention] = useState(false);

    // students and pupil_periods
    const [students, setStudents] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [selectedPeriods, setSelectedPeriods] = useState([]);

    // intervention types (multi select)
    const [selectedInverventionTypes, setSelectedInverventionTypes] = useState([]);

    // student status (multi select)
    const [selectedStudentStatuses, setSelectedStudentStatuses] = useState([]);

    // include low absenteeism
    const [includeNormalAttendance, setIncludeNormalAttendance] = useState(true);

    // include medium & high absenteeism
    const [includeLowAttendance, setIncludeLowAttendance] = useState(false);

    // subject selection
    const [selectedSubject, setSelectedSubject] = useState({name: "Maths - All Areas", subject: 'maths_all_areas'});

    // report validation message
    const [reportValidationMessage, setReportValidationMessage] = useState("");

    // report data
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [reportData, setReportData] = useState([]);

    // pdf
    const groupReportChartRef = useRef();

    // get schools
    useEffect(() => {
        (async () => {
            try {
                const response = await getSchools();
                setSchools(response);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [])

    // get tutors when school is selected
    useEffect(() => {
        if (!selectedSchool.id) {
            return;
        }

        setReportGenerated(false);

        (async () => {
            try {
                console.log('School filter data: ', selectedSchool.filter_data);

                // reset selected dependent fields
                setSelectedTutorGroup({});
                setSelectedTutorGroupLead({});
                setReportValidationMessage("");
                setSelectedPeriods([]);

                // set selectable periods
                const schoolFilterData = JSON.parse(selectedSchool.filter_data);
                const schoolPeriods = schoolFilterData && schoolFilterData.periods ? schoolFilterData.periods : [];
                const schoolTutorGroupLeads = schoolFilterData && schoolFilterData.tutors ? schoolFilterData.tutors : [];

                setPeriods(schoolPeriods);
                setTutorGroupLeads(schoolTutorGroupLeads);
            } catch (error) {
                console.log(error);
            }
        })();
    }, [selectedSchool])

    // get tutor groups when tutor group lead is selected
    useEffect(() => {
        if (!selectedTutorGroupLead.id) {
            return;
        }

        setReportGenerated(false);

        (async () => {
            try {
                console.log('Tutor group lead: ', selectedTutorGroupLead);
                const tutorGroupsData = selectedTutorGroupLead.tutor_groups;

                // prefix tutor group name with tutor name
                tutorGroupsData.forEach(group => {
                    group.name = `${selectedTutorGroupLead.name} - ${group.name}`;
                });

                // // sort alphabetically
                // tutorGroupsData.sort((a, b) => a.name.localeCompare(b.name));

                setTutorGroups(tutorGroupsData);

                // reset selected dependent fields
                setSelectedTutorGroup({});
                setReportValidationMessage("");
            } catch (error) {
                console.log(error);
            }
        })();
    }, [selectedTutorGroupLead])

    // multi select intervention types
    const toggleInverventionTypeSelection = (type) => {
        if (!type) {
            setSelectedInverventionTypes([]);
        }

        setSelectedInverventionTypes((prevTypes) => {
            if (prevTypes.find((t) => t.type === type.type)) {
                // remove type from selected types
                return prevTypes.filter((t) => t.type !== type.type);
            } else {
                // add type to selected types
                return [...prevTypes, type];
            }
        });
    }

    // multi select student status
    const toggleStudentStatusSelection = (status) => {
        if (!status) {
            setSelectedStudentStatuses([]);
        }

        setSelectedStudentStatuses((prevStatuses) => {
            if (prevStatuses.find((s) => s.status === status.status)) {
                // remove status from selected statuses
                return prevStatuses.filter((s) => s.status !== status.status);
            } else {
                // add status to selected statuses
                return [...prevStatuses, status];
            }
        });
    }

    // multi select periods
    const updateSelectedPeriods = (period) => {
        if (!period) {
            setSelectedPeriods([]);
        }

        let newSelectedPeriods = [...selectedPeriods];

        if (newSelectedPeriods.find((s) => s.calendar_year === period.calendar_year && s.period_name === period.period_name)) {
            // remove period from selected periods
            newSelectedPeriods = newSelectedPeriods.filter((s) => s.calendar_year !== period.calendar_year || s.period_name !== period.period_name);
        }
        else {
            // add period to selected periods
            newSelectedPeriods.push(period);
        }

        newSelectedPeriods = newSelectedPeriods.sort((a, b) => {
            if (a.period_name === 'Baseline') {
                return -1;
            }

            // create date from period_name and calendar_year
            const aDate = new Date(`${a.period_name} ${a.calendar_year}`);
            const bDate = new Date(`${b.period_name} ${b.calendar_year}`);

            return aDate - bDate;
        });

        setSelectedPeriods(newSelectedPeriods);
    }

    // drop selected periods when periods change
    useEffect((i) => { 
        if (periods.length < 1) {
            setSelectedPeriods([]);
        } else {
            // set the last period
            setSelectedPeriods([periods[periods.length - 1]]);
        }
    }, [periods])

    // generate report 
    const generateReport = () => {
        if (!selectedSchool.id) {
            return;
        }

        setReportGenerated(false);
        setLoading(true);

        // prepare fetch args
        const args = {
            schoolId: selectedSchool.id,
            includePeriods: true
        };

        // year group
        if (selectedYearGroup.year) {
            args.yearGroup = selectedYearGroup.year;
        }

        // all tutor's tutor groups if not selected and tutor selected
        if (selectedTutorGroupLead.id && !selectedTutorGroup.id) {
            args.tutorGroupId = tutorGroups.map((group) => group.id);
        }

        // selected tutor group if is selected
        if (selectedTutorGroup.id) {
            args.tutorGroupId = selectedTutorGroup.id;
        }

        // fetch
        (async () => {
            try {
                const currentRequestId = Date.now();
                latestRequestIdRef.current = currentRequestId;
                const response = await getStudents(args);

                if (!response) {
                    console.log('No students found for the selected filters');
                    setStudents([]);
                }

                console.log('setting students', response);
                
                response.sort((a, b) => a.name.localeCompare(b.name));
                setStudents(response);
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        })();
    }

    // Trigger report generation (part 2) when students are fetched
    useEffect(() => {
        if (students.length < 1) {
            setReportValidationMessage("No students found for the selected filters");
            setLoading(false);
            return;
        }

        generateReportPartTwo();
    }, [students])

    // generate the report part 2 (handle the front-end filtering when the students are returned)
    const generateReportPartTwo = () => {
        const lastSelectedPeriod = selectedPeriods[selectedPeriods.length - 1];

        // validate report filters
        if (!selectedSchool.id) {
            setReportValidationMessage("Please select a school");
            return;
        }

        if (selectedPeriods.length < 1) {
            setReportValidationMessage("Please select reporting periods");
            return;
        }

        // Checks student against selected intervention selection 'Full year', 'Partial year', 'No intervention'
        const passesCompletenessFilter = (completeness) => {
            if (includeFullYrIntervention && completeness === "complete") {
                return true
            };
            if (includePartialYrIntervention && completeness === "partial") {
                return true
            };
            if (includeNoIntervention && completeness === "none") {
                return true
            };

            return false;
        };

        // Checks student against selected attendance selection 'Normal attendance', 'Low attendance'
        const passesAttendanceFilter = (hadSomeLowAttendance) => {
            console.log('Attendance filter check:', {
                includeNormalAttendance,
                includeLowAttendance,
                hadSomeLowAttendance
            });
            if (includeNormalAttendance && !hadSomeLowAttendance) {
                return true
            };
            if (includeLowAttendance && hadSomeLowAttendance) {
                return true
            };

            return false;
        };

        // Checks student against the list of selected intervention types, it has to match at least one
        const passesInterventionTypeFilter = (studentLastSelectedPeriod) => {
            if (selectedInverventionTypes.length === 0) {
                return true
            };

            return selectedInverventionTypes.some((type) => {
                return studentLastSelectedPeriod.intervention_type === type.type
            });
        };

        // Checks student against the list of selected student statuses it has to match at least one
        const passesStatusFilter = (studentLastSelectedPeriod) => {
            if (selectedStudentStatuses.length === 0) {
                return true
            };

            return selectedStudentStatuses.some((status) => {
                return studentLastSelectedPeriod.pupil_status === status.status
            });
        };


        // Apply all the selected filters to the students
        let newSelectedStudents = students.filter((student) => {
            const studentLastSelectedPeriod = student.pupil_periods.find(
            (period) =>
                period.calendar_year === lastSelectedPeriod.calendar_year &&
                period.period_name === lastSelectedPeriod.period_name
            );

            if (!studentLastSelectedPeriod) {
                return false
            };

            const completeness = calcAcademicYearInterventionCompleteness(
                student.pupil_periods,
                selectedPeriods
            );
            const hadSomeLowAttendance = calcHadSomeLowAttendance(
                student.pupil_periods,
                selectedPeriods
            );

            // Check if student passes all filters, all conditions must be true for student to be included
            const passesFilters =
                passesCompletenessFilter(completeness) &&
                passesAttendanceFilter(hadSomeLowAttendance) &&
                passesInterventionTypeFilter(studentLastSelectedPeriod) &&
                passesStatusFilter(studentLastSelectedPeriod);


            // Log details of the students who are filtered out for debugging purposes
            if (!passesFilters) {
                console.log("Student filtered out:", {
                    name: student.name,
                    completeness,
                    hadSomeLowAttendance,
                    pupil_status: studentLastSelectedPeriod.pupil_status,
                    intervention_type: studentLastSelectedPeriod.intervention_type,
                });
            }

            return passesFilters;
        });

        if (newSelectedStudents.length < 1) {
            setReportValidationMessage("No students meet the selected criteria");
        } else {
            setReportValidationMessage("");
        }

        setSelectedStudents(newSelectedStudents);
        setLoading(false);
    };


    // prepare report data
    useEffect(() => {

        if (!selectedStudents || selectedStudents.length < 1) {
            return;
        }

        let newReportData = [];

        newReportData = [...selectedStudents];

        setReportData(newReportData);
        setReportGenerated(true);

    }, [selectedStudents])

    // pre-select intervention filters on student status selection
    useEffect(() => {
        if (selectedStudentStatuses.length < 1) {
            return;
        }

        selectedStudentStatuses.forEach((status) => {
            if (status.status === 'inactive_not_started' || status.status === 'inactive_finished' || status.status === 'inactive_not_completed_left') {
                setIncludeNoIntervention(true);
                setIncludePartialYrIntervention(true);
            }

            if (status.status === 'active_in_school' || status.status === 'active_off_site' || status.status === 'active_home_educated') {
                setIncludeFullYrIntervention(true);
            }
        });
    }, [selectedStudentStatuses])

    // Any filter change will reset the report
    useEffect(() => {
        setReportValidationMessage("");
        setReportGenerated(false);
        setSelectedStudents([]);
        setReportData([]);
    }, [
        selectedSchool, 
        selectedYearGroup, 
        selectedTutorGroupLead, 
        selectedTutorGroup, 
        selectedInverventionTypes, 
        selectedStudentStatuses, 
        selectedPeriods, 
        includeNormalAttendance, 
        includeLowAttendance, 
        includeFullYrIntervention, 
        includePartialYrIntervention, 
        includeNoIntervention, 
        selectedSubject
    ]);

    // clear filters
    const clearFilters = () => {
        setSelectedSchool({});
        setSelectedYearGroup({});
        setSelectedTutorGroupLead({});
        setSelectedTutorGroup({});
        setSelectedInverventionTypes([]);
        setSelectedStudentStatuses([]);
        setSelectedPeriods([]);
        setIncludeNormalAttendance(true);
        setIncludeLowAttendance(false);
        setIncludeFullYrIntervention(true);
        setIncludePartialYrIntervention(false);
        setIncludeNoIntervention(false);
        setSelectedSubject({});
        setReportValidationMessage("");
        setSelectedStudents([]);
        setLoading(false);
    }

    return (
        <div className="group-reports">
            <h1>Group Reports</h1>

            <div className="full-width-select-cont">
                <CustomSelect
                    name="select-school" 
                    placeholder="Select School"
                    options={schools}
                    labelKey="name"
                    setSelectedOption={(option) => setSelectedSchool(option)}
                    selectedOption={selectedSchool}
                />
            </div>

            <div className="separator"></div>

            <div className="select-report-filters-cont">
                <h3>Select Report Filters</h3>

                <div className="flex-row-select-cont">
                    <CustomSelect
                        name="select-year-group" 
                        placeholder="Select Year Group"
                        options={yearGroups}
                        labelKey="name"
                        setSelectedOption={(option) => setSelectedYearGroup(option)}
                        selectedOption={selectedYearGroup}
                    />

                    <CustomSelect
                        name="select-tutor-group-lead" 
                        placeholder="Select tutor group lead"
                        options={tutorGroupLeads}
                        labelKey="name"
                        setSelectedOption={(option) => setSelectedTutorGroupLead(option)}
                        selectedOption={selectedTutorGroupLead}
                    />

                    <CustomSelect
                        name="select-tutor-group" 
                        placeholder="Select tutor group"
                        options={tutorGroups}
                        labelKey="name"
                        setSelectedOption={(option) => setSelectedTutorGroup(option)}
                        selectedOption={selectedTutorGroup}
                    />
                </div>

                <div className="full-width-select-cont">
                    <CustomMultiSelectPeriod
                        name="period-select" 
                        placeholder="Select reporting periods"
                        options={periods}
                        setSelectedOption={(period) => updateSelectedPeriods(period)}
                        selectedOptions={selectedPeriods}
                    />
                </div>

                <div className="flex-row-checkbox-cont">
                    <CustomCheckbox 
                        name="include-full-yr-intervention" 
                        label="Include full intervention students" 
                        checked={includeFullYrIntervention} 
                        setChecked={(newStatus) => setIncludeFullYrIntervention(newStatus)}
                    />

                    <CustomCheckbox 
                        name="include-partial-yr-intervention" 
                        label="Include partial intervention students" 
                        checked={includePartialYrIntervention} 
                        setChecked={(newStatus) => setIncludePartialYrIntervention(newStatus)}
                    />

                    <CustomCheckbox
                        name="include-no-intervention"
                        label="Include no intervention students"
                        checked={includeNoIntervention}
                        setChecked={(newStatus) => setIncludeNoIntervention(newStatus)}
                    />
                </div>

                <div className="flex-row-select-cont">
                    <CustomMultiSelect
                        name="select-intervention-type" 
                        placeholder="Select intervention type"
                        options={typesOfIntervention}
                        labelKey="name"
                        setSelectedOption={(option) => toggleInverventionTypeSelection(option)}
                        selectedOptions={selectedInverventionTypes}
                    />
                </div>

                <div className="flex-row-select-cont">
                    <CustomMultiSelect
                        name="select-student-status" 
                        placeholder="Select student status"
                        options={studentStatuses}
                        labelKey="name"
                        setSelectedOption={(option) => toggleStudentStatusSelection(option)}
                        selectedOptions={selectedStudentStatuses}
                    />
                </div>

                <div className="flex-row-checkbox-cont">
                    <CustomCheckbox 
                        name="checkbox-normal-attendance" 
                        label="Include normal attendance marked students" 
                        checked={includeNormalAttendance} 
                        setChecked={(newStatus) => setIncludeNormalAttendance(newStatus)}
                    />

                    <CustomCheckbox 
                        name="checkbox-include-low-attendance" 
                        label="Include low attendance marked students" 
                        checked={includeLowAttendance} 
                        setChecked={(newStatus) => setIncludeLowAttendance(newStatus)}
                    />
                </div>

                <div className="full-width-select-cont">
                    <CustomSelect
                        name="select-subject" 
                        placeholder="Select subject / subject area"
                        options={selectableSubjects}
                        labelKey="name"
                        setSelectedOption={(option) => setSelectedSubject(option)}
                        selectedOption={selectedSubject}
                    />
                </div>

                <a onClick={() => clearFilters()} className="clear-filters-btn">Clear Filters</a>
                <button onClick={() => generateReport()} disabled={loading}>{loading ? 'Loading... ' : 'Generate Report'}</button>
                <span className="generate-report-validation-msg">{reportValidationMessage}</span>
            </div>

            <div className="separator"></div>
            

            {selectedStudents && selectedStudents.length > 0 && reportGenerated &&
                <>
                    <div className={loading ? "group-report-cont loading" : "group-report-cont"}>

                        <div className="group-report-details-title">
                            <h2>Group Report</h2>
                        </div>

                        <ul className="report-details-list">
                            <li><b>School</b> - {selectedSchool && selectedSchool.name && selectedSchool.name}</li>
                            {selectedTutorGroupLead && selectedTutorGroupLead.name &&
                                <li>
                                    <b>Intervention lead</b> - <span>{selectedTutorGroupLead.name}</span>
                                </li>
                            }
                            {selectedTutorGroup && selectedTutorGroup.name &&
                                <li>
                                    <b>Tutor Group</b> - <span>{selectedTutorGroup.name}</span>
                                </li>
                            }
                            {selectedYearGroup && selectedYearGroup.year &&
                                <li>
                                    <b>Year Group</b> - <span>{selectedYearGroup.year}</span>
                                </li>
                            }
                            {selectedStudentStatuses && selectedStudentStatuses.length > 0 &&
                                <li>
                                    <b>Student Statuses</b> - 
                                    {selectedStudentStatuses && selectedStudentStatuses[0] && selectedStudentStatuses.map((selectedStatus, index) => 
                                        <>
                                        <span>{selectedStatus.name} {index !== selectedStudentStatuses.length && ', '}</span>
                                        </>
                                    )}
                                </li>
                            }
                            {selectedInverventionTypes && selectedInverventionTypes.length > 0 &&
                                <li>
                                    <b>Intervention Types</b> - 
                                    {selectedInverventionTypes && selectedInverventionTypes[0] && selectedInverventionTypes.map((selectedType, index) => 
                                        <>
                                        <span>{selectedType.name} {index !== selectedInverventionTypes.length && ', '}</span>
                                        </>
                                    )}
                                </li>
                            }
                            <li><b>Reporting Periods</b> - 
                                {selectedPeriods && selectedPeriods[0] && selectedPeriods.map((selectedPeriod, index) => 
                                    <>
                                    <span>{selectedPeriod.period_name} - {selectedPeriod.calendar_year} {index !== selectedPeriods.length && ', '}</span>
                                    </>
                                )}
                            </li>
                            <li><b>Subject / Areas</b> - 
                                {selectedSubject && selectedSubject.name &&
                                    <>
                                    <span>{selectedSubject.name}</span>
                                    </>
                                }
                            </li>
                        </ul>

                        <div ref={groupReportChartRef}>
                            <GroupReportBarChart 
                                selectedStudents={selectedStudents} 
                                selectedPeriods={selectedPeriods}
                                selectedSubject={selectedSubject}
                            />
                        </div>
                    </div>

                    <GroupReportsPDF
                        groupReportChartRef={groupReportChartRef}
                        selectedPeriods={selectedPeriods}
                        selectedSchool={selectedSchool}
                        selectedYearGroup={selectedYearGroup}
                        selectedInverventionTypes={selectedInverventionTypes}
                        selectedStudentStatuses={selectedStudentStatuses}
                        includeLowAttendance={includeLowAttendance}
                        selectedTutorGroupLead={selectedTutorGroupLead}
                        selectedTutorGroup={selectedTutorGroup}
                        selectedSubject={selectedSubject}
                    />
                </>
            }

            
        </div>
    );
}

export default GroupReports;
